import { REACTIONS, type Reaction } from '@campus/core';

/**
 * Smileys tapés au clavier (« :) », « <3 »…) remplacés par l'emoji du catalogue,
 * et emojis du catalogue trouvés dans un texte (affichés en images Fluent).
 */

const SMILEYS: Record<string, string> = {
  ':)': '🙂', ':-)': '🙂',
  ':D': '😁', ':-D': '😁',
  ';)': '😉', ';-)': '😉',
  ':(': '😢', ':-(': '😢', ":'(": '😢',
  ':P': '😛', ':p': '😛', ':-P': '😛', ':-p': '😛',
  ':o': '😮', ':O': '😮', ':-o': '😮', ':-O': '😮',
  'xD': '😂', 'XD': '😂',
  '<3': '❤️',
  'B)': '😎', 'B-)': '😎',
};

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const SMILEY_KEYS = Object.keys(SMILEYS).sort((a, b) => b.length - a.length).map(escape).join('|');
/** Un smiley seulement entre deux espaces (ou en début de texte) : jamais dans « http://… » ni « a:p ». */
const SMILEY_BEFORE_SPACE = new RegExp(`(^|\\s)(${SMILEY_KEYS})(?=\\s)`, 'g');
const SMILEY_AT_END = new RegExp(`(^|\\s)(${SMILEY_KEYS})$`);

/**
 * Pendant la frappe : remplace les smileys suivis d'un espace ou d'un retour
 * à la ligne. Renvoie le texte et la nouvelle place du curseur.
 */
export function replaceTypedSmileys(text: string, caret: number): { text: string; caret: number } {
  let shift = 0;
  const out = text.replace(SMILEY_BEFORE_SPACE, (_m, pre: string, smiley: string, offset: number) => {
    const emoji = SMILEYS[smiley]!;
    if (offset + pre.length < caret) shift += emoji.length - smiley.length;
    return pre + emoji;
  });
  return { text: out, caret: caret + shift };
}

/** À l'envoi : le dernier smiley, que rien ne suit. */
export function replaceFinalSmiley(text: string): string {
  return replaceTypedSmileys(text, text.length).text
    .replace(SMILEY_AT_END, (_m, pre: string, smiley: string) => pre + SMILEYS[smiley]!);
}

const BY_CHAR = new Map<string, Reaction>();
for (const r of REACTIONS) {
  BY_CHAR.set(r.char, r);
  BY_CHAR.set(r.char.replace(/\uFE0F/g, ''), r);
}
// « ❤ » et « ❤️ » (avec ou sans sélecteur d'emoji) donnent le même cœur.
const EMOJI_SPLIT = new RegExp(
  `(${[...BY_CHAR.keys()].sort((a, b) => b.length - a.length).map(escape).join('|')})\uFE0F?`,
  'gu',
);

export type EmojiPart = { text: string } | { reaction: Reaction };

/** Coupe un texte entre morceaux de texte et emojis du catalogue. */
export function splitEmojis(text: string): EmojiPart[] {
  const parts: EmojiPart[] = [];
  let last = 0;
  for (const m of text.matchAll(EMOJI_SPLIT)) {
    const r = BY_CHAR.get(m[1]!);
    if (!r) continue;
    if (m.index! > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ reaction: r });
    last = m.index! + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/** Message fait seulement d'emojis (3 au plus) : affiché en grand, comme WhatsApp. */
export function isEmojiOnly(text: string): boolean {
  const parts = splitEmojis(text.trim());
  const emojis = parts.filter((p) => 'reaction' in p).length;
  return emojis > 0 && emojis <= 3 && parts.every((p) => 'reaction' in p || !p.text.trim());
}
